import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';

import { useTiersService } from '@europrocurement/l2d-domain';
import { type DataProviderProps, type DataState } from '@europrocurement/l2d-modules/providers';
import { GetNotesContactContactsItem200ResponseNotesInner } from '@europrocurement/l2d-domain/openApi/ApiTiers';

const MAX_NOTES_FETCH_ATTEMPTS = 3;

type CurrentPublisherProviderState = DataState & {
    data:
        | {
              id: number | null;
              notes: GetNotesContactContactsItem200ResponseNotesInner[];
          }
        | undefined;
    currentIdPublisher: number | null | undefined;
    notesFetchFailed: boolean;
    notesFetchAttempt: number;
};

type CurrentPublisherProviderProps = DataProviderProps & {
    idPublisher: string;
};

const INIT_CURRENT_PUBLISHER_CTX = 'init_current_publisher_ctx';
const SET_CURRENT_PUBLISHER_CTX = 'set_current_publisher_ctx';
const SET_CURRENT_PUBLISHER_NOTES_CTX = 'set_current_publisher_notes_ctx';
const REFRESH_CURRENT_PUBLISHER_CTX = 'refresh_current_publisher_ctx';
const NOTES_FETCH_FAILED_CTX = 'notes_fetch_failed_ctx';
const RETRY_NOTES_CTX = 'retry_notes_ctx';
const NOTES_FETCH_ATTEMPT_CTX = 'notes_fetch_attempt_ctx';

type Action =
    | {
          type: 'init_current_publisher_ctx';
          payload: { id: number; notes: GetNotesContactContactsItem200ResponseNotesInner[] };
      }
    | {
          type: 'set_current_publisher_ctx';
          payload: { id: number; notes: GetNotesContactContactsItem200ResponseNotesInner[] };
      }
    | {
          type: 'set_current_publisher_notes_ctx';
          payload: GetNotesContactContactsItem200ResponseNotesInner[];
      }
    | { type: 'refresh_current_publisher_ctx' }
    | { type: 'notes_fetch_failed_ctx' }
    | { type: 'retry_notes_ctx' }
    | { type: 'notes_fetch_attempt_ctx'; payload: number };

const CurrentPublisherContext = createContext<
    (CurrentPublisherProviderState & { isFetchingNotes: boolean }) | null
>(null);
const CurrentPublisherDispatchContext = createContext<React.Dispatch<Action> | null>(null);
const CurrentPublisherActionsContext = createContext<{ retryNotes: () => void } | null>(null);

function CurrentPublisherReducer(
    state: CurrentPublisherProviderState,
    action: Action,
): CurrentPublisherProviderState {
    switch (action.type) {
        case INIT_CURRENT_PUBLISHER_CTX: {
            return {
                ...state,
                loaded: true,
                currentIdPublisher: action.payload.id,
                data: action.payload,
                notesFetchFailed: false,
                notesFetchAttempt: 0,
            };
        }

        case SET_CURRENT_PUBLISHER_CTX: {
            return {
                ...state,
                data: action.payload,
            };
        }

        case SET_CURRENT_PUBLISHER_NOTES_CTX: {
            return {
                ...state,
                data: {
                    ...state.data,
                    notes: action.payload,
                } as CurrentPublisherProviderState['data'],
            };
        }

        case REFRESH_CURRENT_PUBLISHER_CTX: {
            return {
                ...state,
                loaded: false,
            };
        }

        case NOTES_FETCH_FAILED_CTX: {
            return {
                ...state,
                notesFetchFailed: true,
            };
        }

        case RETRY_NOTES_CTX: {
            return {
                ...state,
                loaded: false,
                notesFetchFailed: false,
                notesFetchAttempt: 0,
            };
        }

        case NOTES_FETCH_ATTEMPT_CTX: {
            return {
                ...state,
                notesFetchAttempt: action.payload,
            };
        }

        default: {
            const exhaustiveCheck: never = action;
            throw new Error(`Unknown action: ${exhaustiveCheck}`);
        }
    }
}

const CurrentPublisherProvider: React.FunctionComponent<
    PropsWithChildren<CurrentPublisherProviderProps>
> = function ({ autoload, children, idPublisher }) {
    const [state, dispatch] = useReducer(CurrentPublisherReducer, {
        loaded: false,
        data: undefined,
        currentIdPublisher: undefined,
        notesFetchFailed: false,
        notesFetchAttempt: 0,
    } as CurrentPublisherProviderState);

    const { publishersApi } = useTiersService();
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const fetchAndInitPublisher = React.useCallback(
        async (publisherId: string) => {
            const publisherIntId = parseInt(publisherId, 10);

            const tryFetch = async (attempt: number): Promise<void> => {
                dispatch({ type: NOTES_FETCH_ATTEMPT_CTX, payload: attempt });
                try {
                    const notesResult = await publishersApi.getNotesFournisseurFournisseurItem({
                        id: publisherIntId,
                        limit: 100,
                    });
                    const notes = notesResult.data.notes || [];
                    dispatch({
                        type: INIT_CURRENT_PUBLISHER_CTX,
                        payload: { id: publisherIntId, notes },
                    });
                } catch (error) {
                    console.error(
                        `[CurrentPublisherProvider] Notes fetch attempt ${attempt}/${MAX_NOTES_FETCH_ATTEMPTS} failed:`,
                        error,
                    );
                    if (attempt < MAX_NOTES_FETCH_ATTEMPTS) {
                        await new Promise<void>((r) => {
                            setTimeout(r, 1000 * attempt);
                        });
                        await tryFetch(attempt + 1);
                    } else {
                        dispatch({ type: NOTES_FETCH_FAILED_CTX });
                    }
                }
            };

            setIsFetching(true);
            try {
                await tryFetch(1);
            } finally {
                setIsFetching(false);
            }
        },
        [publishersApi],
    );

    const retryNotes = React.useCallback(() => {
        dispatch({ type: RETRY_NOTES_CTX });
    }, []);

    useEffect(() => {
        if (
            !autoload ||
            state.loaded === true ||
            isFetching === true ||
            state.notesFetchFailed === true
        ) {
            return;
        }
        fetchAndInitPublisher(idPublisher);
    }, [
        publishersApi,
        autoload,
        state.loaded,
        state.notesFetchFailed,
        isFetching,
        fetchAndInitPublisher,
        idPublisher,
    ]);

    useEffect(() => {
        if (
            state.currentIdPublisher &&
            state.currentIdPublisher !== parseInt(idPublisher, 10) &&
            autoload &&
            state.loaded === true &&
            !isFetching
        ) {
            dispatch({
                type: REFRESH_CURRENT_PUBLISHER_CTX,
            });
        }
    }, [autoload, idPublisher, isFetching, state.currentIdPublisher, state.loaded]);

    const ctxValue = React.useMemo(
        () => ({ ...state, isFetchingNotes: isFetching }),
        [state, isFetching],
    );

    const actionsCtxValue = React.useMemo(() => ({ retryNotes }), [retryNotes]);

    return (
        <CurrentPublisherContext.Provider value={ctxValue}>
            <CurrentPublisherDispatchContext.Provider value={dispatch}>
                <CurrentPublisherActionsContext.Provider value={actionsCtxValue}>
                    {children}
                </CurrentPublisherActionsContext.Provider>
            </CurrentPublisherDispatchContext.Provider>
        </CurrentPublisherContext.Provider>
    );
};

const useCurrentPublisherCtx = () => {
    const state = useContext(CurrentPublisherContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getCurrentPublisher = () => state?.data;

    return {
        publisher: state?.data,
        isDataLoaded,
        getCurrentPublisher,
        notesFetchFailed: state?.notesFetchFailed ?? false,
        notesFetchAttempt: state?.notesFetchAttempt ?? 0,
        isFetchingNotes: state?.isFetchingNotes ?? false,
    };
};

const useCurrentPublisherCtxDispatch = () => useContext(CurrentPublisherDispatchContext);

const useCurrentPublisherActions = () => useContext(CurrentPublisherActionsContext);

export {
    CurrentPublisherProvider,
    CurrentPublisherContext,
    CurrentPublisherDispatchContext,
    CurrentPublisherActionsContext,
    useCurrentPublisherCtx,
    useCurrentPublisherCtxDispatch,
    useCurrentPublisherActions,
    INIT_CURRENT_PUBLISHER_CTX,
    SET_CURRENT_PUBLISHER_CTX,
    SET_CURRENT_PUBLISHER_NOTES_CTX,
    REFRESH_CURRENT_PUBLISHER_CTX,
    NOTES_FETCH_FAILED_CTX,
    RETRY_NOTES_CTX,
    NOTES_FETCH_ATTEMPT_CTX,
    MAX_NOTES_FETCH_ATTEMPTS,
};
